import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Anchor as AnchorIcon, CheckCircle2, Clock, ExternalLink, Loader2, Lock,
  RefreshCw, Send, Ticket, Unlink, Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import client from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import GameModal from '../components/GameModal';
import { useGamePage } from '../hooks/useGamePage';
import './Anchor.css';

// Gömülü sahnenin süresi; Anchor.css'teki zaman çizelgesiyle eşleşmelidir.
const ANCHOR_SCENE_MS = 4200;
// Video kullanılıyorsa gerçek süresi beklenir, ama sağlayıcı/dosya hatasında
// oyuncu ekranda kilitli kalmasın diye bir üst sınır konur.
const VIDEO_TIMEOUT_MS = 30000;

// Üretilen bağlama bağlantısı sekme belleğinde tutulur: pencere açılamayıp
// aynı sekmede yönlendirildiğinde React durumu kaybolur ve oyuncu geri
// döndüğünde yedek bağlantıyı bulamazdı. Ömrü sunucudaki jetonla aynıdır.
const LINK_STORAGE_KEY = 'anchor:telegram-link-v1';
const LINK_STORAGE_TTL_MS = 30 * 60 * 1000;

const readStoredLink = () => {
  try {
    const raw = window.sessionStorage.getItem(LINK_STORAGE_KEY);
    if (!raw) return '';
    const { url, at } = JSON.parse(raw);
    if (!url || !at || Date.now() - at > LINK_STORAGE_TTL_MS) {
      window.sessionStorage.removeItem(LINK_STORAGE_KEY);
      return '';
    }
    return url;
  } catch {
    return '';
  }
};

const storeLink = (url) => {
  try {
    if (url) {
      window.sessionStorage.setItem(
        LINK_STORAGE_KEY, JSON.stringify({ url, at: Date.now() }));
    } else {
      window.sessionStorage.removeItem(LINK_STORAGE_KEY);
    }
  } catch {
    // Gizli kip / kota dolu: yedek bağlantı kalıcı olmaz, akış yine çalışır.
  }
};

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', maximumFractionDigits: 2,
}).format(Number(value || 0));

const humanizeRemaining = (milliseconds) => {
  const total = Math.max(0, Math.ceil(milliseconds / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours} sa ${minutes} dk`;
  if (minutes > 0) return `${minutes} dk ${seconds} sn`;
  return `${seconds} sn`;
};

const AnchorScene = ({ throwing }) => (
  <div className={`anchor-stage ${throwing ? 'anchor-stage--throwing' : ''}`}>
    <div className="anchor-stage__sky" />
    <div className="anchor-stage__sea">
      <div className="anchor-wave anchor-wave--back" />
      <div className="anchor-wave" />
    </div>
    <div className="anchor-rope" />
    <svg className="anchor-icon" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="5" r="3" />
      <line x1="12" y1="22" x2="12" y2="8" />
      <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
    </svg>
    <div className="anchor-splash" />
    <div className="anchor-splash anchor-splash--late" />
    <svg className="anchor-chest" viewBox="0 0 24 24" fill="currentColor"
      aria-hidden="true">
      <path d="M3 9a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v1H3V9Zm0 3h8v2h2v-2h8v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6Z" />
    </svg>
  </div>
);

export const freespinWaitSeconds = (availableAt, now = Date.now()) => {
  const readyAt = new Date(availableAt || 0).getTime();
  if (!Number.isFinite(readyAt) || readyAt <= now) return 0;
  return Math.ceil((readyAt - now) / 1000);
};

export const FreespinResult = ({ reward, onPlay }) => {
  const [clock, setClock] = useState(() => Date.now());
  const waitSeconds = freespinWaitSeconds(reward.available_at, clock);

  useEffect(() => {
    if (!waitSeconds) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [waitSeconds]);

  return (
    <div className="text-sm text-emerald-200">
      <p>
        <strong>{reward.spin_count} freespin</strong> hakkın{' '}
        <strong>
          {reward.game?.name
            ? `${reward.game.name} oyununa`
            : 'seçili oyuna'}
        </strong>{' '}
        tanımlandı.
      </p>
      <p className="mt-1 text-xs text-emerald-100/70">
        {waitSeconds > 0
          ? `Sağlayıcıda etkinleşiyor; bakiyenden spin gitmemesi için ${waitSeconds} saniye sonra oyunu aç.`
          : 'Oyunu gerçek modda açtığında sağlayıcının freespin bildirimi otomatik görünür.'}
      </p>
      {reward.game?.id && reward.game?.active && (
        <button
          type="button"
          disabled={waitSeconds > 0}
          onClick={() => onPlay(reward.game)}
          className="mt-3 mx-auto px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:bg-amber-400 disabled:text-amber-950 disabled:cursor-wait text-emerald-950 text-xs font-black flex items-center justify-center gap-2"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {waitSeconds > 0
            ? `Etkinleşiyor (${waitSeconds} sn)`
            : 'Freespinleri Oyna'}
        </button>
      )}
    </div>
  );
};

const Anchor = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [throwing, setThrowing] = useState(false);
  const [claiming, setClaiming] = useState('');
  // Sahne bittikten sonra tür seçimi için bekleyen atış.
  const [pending, setPending] = useState(null);
  const [reward, setReward] = useState(null);
  const [activeRewardGame, setActiveRewardGame] = useState(null);
  // Oyun sayfası kullanan şemada (Betrun) ödül oyunu pencerede değil kendi
  // sayfasında açılır (bkz. hooks/useGamePage.js).
  const openGamePage = useGamePage();
  const playRewardGame = useCallback(
    (game) => { if (!openGamePage(game)) setActiveRewardGame(game); },
    [openGamePage],
  );
  const [linking, setLinking] = useState(false);
  // Üretilmiş derin bağlantı: otomatik açılış her koşulda çalışmadığı için
  // oyuncuya tıklanabilir bir yedek bağlantı olarak da sunulur.
  const [linkUrl, setLinkUrl] = useState(readStoredLink);
  const [unlinking, setUnlinking] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const sceneTimer = useRef(null);
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const userId = user?.id;

  // ``silent`` tazelemede tam sayfa yükleme ekranı gösterilmez; arka planda
  // yapılan yoklamanın sayfayı boşaltması oyuncuya arıza gibi görünürdü.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await client.get('/anchor');
      setState(data);
      setLoadError('');
      // Bağlama tamamlandıysa yedek bağlantının işi bitmiştir.
      if (data.telegram?.linked) {
        setLinkUrl('');
        storeLink('');
      }
      // Tür seçmeden ayrılan oyuncu kaldığı yerden devam eder.
      if (data.pending_throw) setPending(data.pending_throw);
    } catch (error) {
      if (!silent) {
        setLoadError(
          error.response?.data?.detail || 'Çapa etkinliği yüklenemedi');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, userId]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => () => clearTimeout(sceneTimer.current), []);

  const nextAt = state?.next_throw_at
    ? new Date(state.next_throw_at).getTime() : 0;
  const remaining = nextAt ? nextAt - now : 0;
  const canThrow = Boolean(state?.can_throw) && !throwing && !pending;
  const telegram = state?.telegram || null;
  // Bağlantı üretildi ama hesap henüz bağlanmadı: oyuncu Telegram'da.
  const awaitingLink = Boolean(linkUrl) && !telegram?.linked;

  // Telegram uygulamasına geçen oyuncu geri döndüğünde durumu elle
  // yenilemek zorunda kalmasın; sekme tekrar görünür olunca sessizce sorulur.
  useEffect(() => {
    if (!awaitingLink) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [awaitingLink, load]);

  const prizeValues = useMemo(
    () => (state?.prizes || []).map((prize) => prize.value),
    [state],
  );

  // Sahne bitince tür seçimi açılır. Video varsa gerçek bitişini bekleriz;
  // yoksa CSS sahnesinin sabit süresi kullanılır.
  const startScene = useCallback((throwDoc) => {
    setThrowing(true);
    const finish = () => {
      setThrowing(false);
      setPending(throwDoc);
    };
    const videoUrl = state?.video_url;
    if (videoUrl && videoRef.current) {
      const video = videoRef.current;
      video.currentTime = 0;
      const play = video.play();
      if (play?.catch) play.catch(() => {});
      sceneTimer.current = setTimeout(finish, VIDEO_TIMEOUT_MS);
      video.onended = () => {
        clearTimeout(sceneTimer.current);
        finish();
      };
      return;
    }
    sceneTimer.current = setTimeout(finish, ANCHOR_SCENE_MS);
  }, [state]);

  const handleThrow = async () => {
    if (!user) {
      navigate('/', { state: { auth: 'login' } });
      return;
    }
    if (!canThrow) return;
    setReward(null);
    try {
      const { data } = await client.post('/anchor/throw');
      if (data.resumed) {
        // Sunucu yarım kalan atışı döndürdü; sahneyi tekrar oynatmaya gerek
        // yok, doğrudan tür seçimine geçilir.
        setPending(data.throw);
        return;
      }
      startScene(data.throw);
    } catch (error) {
      toast({
        title: 'Çapa atılamadı',
        description: error.response?.data?.detail
          || 'İşlem tamamlanamadı, lütfen tekrar deneyin',
        variant: 'destructive',
      });
      load();
    }
  };

  const handleClaim = async (rewardType) => {
    if (!pending || claiming) return;
    setClaiming(rewardType);
    try {
      const { data } = await client.post('/anchor/claim', {
        throw_id: pending.id,
        reward_type: rewardType,
      });
      const result = { ...data.result, value: pending.value };
      setReward(result);
      setPending(null);
      const freespinReady = (
        rewardType === 'freespin' && result.status === 'completed'
      );
      toast({
        title: rewardType === 'freebet' || freespinReady
          ? 'Ödülünüz tanımlandı'
          : 'Freespin ödülünüz sıraya alındı',
        description: rewardType === 'freebet'
          ? `${money(pending.value)} freebet hesabınıza eklendi`
          : freespinReady
            ? `${result.spin_count} freespin hakkınız ${result.game?.name ? `${result.game.name} oyununa` : 'seçili oyuna'} tanımlandı`
            : `${result.spin_count} freespin hakkınız sağlayıcı tarafından işleniyor`,
      });
      load();
    } catch (error) {
      toast({
        title: 'Ödül alınamadı',
        description: error.response?.data?.detail
          || 'İşlem tamamlanamadı, lütfen tekrar deneyin',
        variant: 'destructive',
      });
    } finally {
      setClaiming('');
    }
  };

  const handleTelegramLink = async () => {
    if (linking) return;
    setLinking(true);
    // Safari (özellikle iOS) yalnızca kullanıcı jestinin **içinde** açılan
    // pencereye izin verir; jeton isteği beklendikten sonra çağrılan
    // ``window.open`` popup sayılıp sessizce engellenir — oyuncu düğmeye
    // basar, hiçbir şey olmaz. Bu yüzden sekme daha jest sürerken rezerve
    // edilir, adresi jeton gelince yazılır.
    //
    // ``noopener`` seçeneği burada bilinçli olarak verilmez: verildiğinde
    // tarayıcı pencere referansı yerine ``null`` döndürür ve adres hiç
    // yazılamaz. Bağı ``opener`` alanını temizleyerek koparıyoruz.
    let popup = null;
    try {
      popup = window.open('', '_blank');
      if (popup) popup.opener = null;
    } catch {
      popup = null;
    }
    try {
      const { data } = await client.post('/telegram/link');
      setLinkUrl(data.url);
      storeLink(data.url);
      let opened = false;
      if (popup && !popup.closed) {
        try {
          popup.location.replace(data.url);
          opened = true;
        } catch {
          opened = false;
        }
      }
      if (opened) {
        toast({
          title: 'Telegram’a yönlendirildiniz',
          description: 'Botta “Başlat” dedikten sonra bu sayfaya dönün',
        });
      } else {
        // Pencere engellendi. Aynı sekmede yönlendirmek popup denetimine
        // takılmaz ve mobilde doğrudan Telegram uygulamasını açar; oyuncu
        // geri döndüğünde durum kendiliğinden tazelenir.
        window.location.href = data.url;
      }
    } catch (error) {
      if (popup && !popup.closed) popup.close();
      toast({
        title: 'Bağlantı oluşturulamadı',
        description: error.response?.data?.detail
          || 'Telegram bağlantısı şu anda üretilemiyor',
        variant: 'destructive',
      });
    } finally {
      setLinking(false);
    }
  };

  const handleTelegramUnlink = async () => {
    if (!window.confirm(
      'Telegram hesabınızın site üyeliğiyle bağlantısı kaldırılsın mı?')) return;
    setUnlinking(true);
    try {
      await client.delete('/telegram/link');
      setLinkUrl('');
      storeLink('');
      toast({
        title: 'Telegram bağlantısı kaldırıldı',
        description: 'İsterseniz farklı bir Telegram hesabını bağlayabilirsiniz.',
      });
      await load();
    } catch (error) {
      toast({
        title: 'Bağlantı kaldırılamadı',
        description: error.response?.data?.detail
          || 'Lütfen tekrar deneyin.',
        variant: 'destructive',
      });
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-20 grid place-items-center">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </main>
    );
  }

  if (loadError || !state) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-sm text-gray-400">{loadError || 'Etkinlik yüklenemedi'}</p>
      </main>
    );
  }

  if (!state.enabled) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-16 text-center">
        <AnchorIcon className="w-10 h-10 mx-auto text-gray-500 mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">{state.title}</h1>
        <p className="text-sm text-gray-400">
          Çapa etkinliği şu anda kapalı. Yakında tekrar açılacak.
        </p>
      </main>
    );
  }

  const statusMessage = () => {
    if (throwing) return 'Çapa suya iniyor...';
    if (pending) return 'Ödülünü seç';
    if (canThrow) return 'Günlük çapa hakkın hazır';
    if (state.reason === 'cooldown' && remaining > 0) {
      return `Yeni hakkına ${humanizeRemaining(remaining)} kaldı`;
    }
    return state.message || 'Çapayı şu anda atamazsın';
  };

  return (
    <>
      <main className="max-w-5xl mx-auto px-4 py-10">
      <header className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-black gold-text">
          {state.title}
        </h1>
        {state.description && (
          <p className="text-sm text-gray-400 mt-3 max-w-2xl mx-auto whitespace-pre-line">
            {state.description}
          </p>
        )}
      </header>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start">
        <section className="pusu-panel p-4 sm:p-6">
          <div className="relative">
            {state.video_url ? (
              <video
                ref={videoRef}
                src={state.video_url}
                className="w-full rounded-2xl bg-black aspect-video object-cover"
                playsInline
                muted
                preload="auto"
              />
            ) : (
              <AnchorScene throwing={throwing} />
            )}

            {pending && (
              <div className="anchor-reveal">
                <div className="text-center px-6">
                  <p className="text-xs uppercase tracking-widest text-yellow-300/80 mb-1">
                    Çapadan çıkan
                  </p>
                  <div className="anchor-reveal__value">{pending.value}</div>
                  <p className="text-sm text-gray-300 mt-2">
                    Ödülünü nasıl almak istersin?
                  </p>
                </div>
              </div>
            )}
          </div>

          {pending ? (
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              <button
                type="button"
                className="anchor-choice"
                disabled={Boolean(claiming)}
                onClick={() => handleClaim('freespin')}
              >
                <span className="flex items-center gap-2 text-yellow-300 font-bold">
                  {claiming === 'freespin'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Zap className="w-4 h-4" />}
                  {pending.value} Freespin
                </span>
                <span className="text-xs text-gray-400">
                  Kampanyaya bağlı casino oyununda geçerli ücretsiz dönüş
                </span>
              </button>
              <button
                type="button"
                className="anchor-choice"
                disabled={Boolean(claiming)}
                onClick={() => handleClaim('freebet')}
              >
                <span className="flex items-center gap-2 text-emerald-300 font-bold">
                  {claiming === 'freebet'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Ticket className="w-4 h-4" />}
                  {money(pending.value)} Freebet
                </span>
                <span className="text-xs text-gray-400">
                  Spor kuponlarında geçerli ücretsiz bahis
                  {state.freebet_min_odd > 1
                    && ` • En az ${Number(state.freebet_min_odd).toFixed(2)} oran`}
                </span>
              </button>
            </div>
          ) : (
            <div className="mt-5 flex flex-col items-center gap-3">
              <p className="text-sm text-gray-400">{statusMessage()}</p>
              <button
                type="button"
                onClick={handleThrow}
                disabled={!canThrow && Boolean(user)}
                className="px-8 py-3 rounded-full font-black text-black bg-gradient-to-b from-yellow-300 to-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {throwing
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <AnchorIcon className="w-5 h-5" />}
                {user ? 'ÇAPAYI AT' : 'GİRİŞ YAP'}
              </button>
            </div>
          )}

          {reward && (
            <div className="mt-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
              <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-400 mb-2" />
              {reward.reward_type === 'freebet' ? (
                <p className="text-sm text-emerald-200">
                  <strong>{money(reward.amount)}</strong> freebet hakkın kupon
                  ekranında hazır.
                  {reward.expires_at && (
                    <> Son kullanım:{' '}
                      {new Date(reward.expires_at).toLocaleDateString('tr-TR')}
                    </>
                  )}
                </p>
              ) : reward.status === 'completed' ? (
                <FreespinResult
                  reward={reward}
                  onPlay={playRewardGame}
                />
              ) : (
                <p className="text-sm text-emerald-200">
                  <strong>{reward.spin_count} freespin</strong> hakkın alındı ve
                  tanımlanmak üzere sıraya girdi. Kısa süre içinde hesabına
                  yüklenecek.
                </p>
              )}
            </div>
          )}
        </section>

        <aside className="space-y-4">
          {telegram?.required && !telegram.member && (
            <div className="pusu-panel p-5">
              <div className="flex items-center gap-2 mb-2">
                <Send className="w-4 h-4 text-sky-400" />
                <h2 className="text-sm font-bold text-white">Telegram şartı</h2>
              </div>
              {!telegram.linked ? (
                <>
                  <p className="text-xs text-gray-400 mb-3">
                    Çapayı atabilmek için Telegram hesabını bağlaman gerekiyor.
                  </p>
                  <button
                    type="button"
                    onClick={handleTelegramLink}
                    disabled={linking}
                    className="w-full py-2 rounded-lg bg-sky-500/90 hover:bg-sky-500 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {linking
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <ExternalLink className="w-4 h-4" />}
                    Telegram hesabımı bağla
                  </button>
                  {linkUrl && (
                    // Tarayıcı otomatik açılışı engellediyse oyuncunun kendi
                    // dokunuşu her zaman geçer; bağlantı 30 dakika geçerlidir.
                    <a
                      href={linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-2 text-center text-xs text-sky-300 underline"
                    >
                      Telegram açılmadıysa buraya dokunun
                    </a>
                  )}
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-400 mb-3">
                    {telegram.error
                      ? `Üyelik doğrulanamadı: ${telegram.error}`
                      : 'Hesabın bağlı ama henüz grubumuza katılmamışsın.'}
                  </p>
                  {state.telegram_url && (
                    <a
                      href={state.telegram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2 rounded-lg bg-sky-500/90 hover:bg-sky-500 text-white text-sm font-bold flex items-center justify-center gap-2 mb-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Gruba katıl
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={handleTelegramUnlink}
                    disabled={unlinking}
                    className="w-full py-2 rounded-lg border border-rose-400/30 text-rose-300 text-xs flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {unlinking
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Unlink className="w-3.5 h-3.5" />}
                    Telegram bağlantısını kaldır
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => load()}
                className="w-full mt-2 py-2 rounded-lg border border-white/10 text-gray-300 text-xs flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Durumu yenile
              </button>
            </div>
          )}

          {telegram?.required && telegram.member && (
            <div className="pusu-panel p-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <p className="text-xs text-gray-300">
                  Telegram grup üyeliğin doğrulandı
                  {telegram.username ? ` (@${telegram.username})` : ''}.
                </p>
              </div>
              <button
                type="button"
                onClick={handleTelegramUnlink}
                disabled={unlinking}
                className="w-full mt-3 py-2 rounded-lg border border-rose-400/30 text-rose-300 text-xs flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {unlinking
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Unlink className="w-3.5 h-3.5" />}
                Telegram bağlantısını kaldır
              </button>
            </div>
          )}

          <div className="pusu-panel p-5">
            <h2 className="text-sm font-bold text-white mb-3">
              Çıkabilecek ödüller
            </h2>
            <div className="flex flex-wrap gap-2">
              {prizeValues.length === 0 ? (
                <p className="text-xs text-gray-400">
                  Henüz ödül tanımlanmamış.
                </p>
              ) : (
                (state.prizes || []).map((prize) => (
                  <span
                    key={prize.id}
                    className="px-3 py-1.5 rounded-lg text-sm font-black text-black"
                    style={{ background: prize.color }}
                  >
                    {prize.value}
                  </span>
                ))
              )}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Her değeri <strong>freespin</strong> ya da <strong>freebet</strong>{' '}
              olarak alabilirsin; sayı ikisinde de aynıdır.
            </p>
          </div>

          <div className="pusu-panel p-5 space-y-2 text-xs text-gray-400">
            <h2 className="text-sm font-bold text-white mb-1">Kurallar</h2>
            <p className="flex items-start gap-2">
              <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {state.cooldown_hours > 0
                ? `${state.cooldown_hours} saatte bir çapa atabilirsin.`
                : 'Çapa hakkında sınır yok.'}
            </p>
            <p className="flex items-start gap-2">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Etkinlik IP başına bir kez geçerlidir.
            </p>
            <p className="flex items-start gap-2">
              <Zap className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Freespin kazancı casino, freebet kazancı spor alanında geçerlidir.
            </p>
            {state.min_deposit > 0 && (
              <p className="flex items-start gap-2">
                <Ticket className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                En az {money(state.min_deposit)} toplam yatırım gerekir.
              </p>
            )}
            {state.freebet_expiry_days > 0 && (
              <p className="flex items-start gap-2">
                <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Freebet hakkı {state.freebet_expiry_days} gün içinde
                kullanılmalıdır.
              </p>
            )}
          </div>
        </aside>
      </div>
      </main>
      {activeRewardGame && (
        <GameModal
          game={activeRewardGame}
          mode="real"
          onClose={() => setActiveRewardGame(null)}
        />
      )}
    </>
  );
};

export default Anchor;
